import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, LogOut, User } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_modular-ui-4/artifacts/m24395kj_logo.png";
import { useAuth } from "@/context/AuthContext";
import BrutalButton from "@/components/common/BrutalButton";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const isAuthed = user && typeof user === "object";

    return (
        <header
            data-testid="site-navbar"
            className="fixed top-0 left-0 right-0 z-50 bg-cream border-b-4 border-ink"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-2.5 group"
                >
                    <span className="w-11 h-11 rounded-xl bg-ink border-2 border-ink shadow-brutal-sm flex items-center justify-center overflow-hidden group-hover:rotate-[-6deg] transition-transform">
                        <img src={LOGO_URL} alt="Technovale Solutions" className="w-full h-full object-contain" />
                    </span>
                    <span className="font-display text-xl sm:text-2xl font-extrabold tracking-tight leading-none">
                        Technovale<span className="block text-xs font-mono text-ink/60 mt-0.5 tracking-widest">SOLUTIONS</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-4 py-2 rounded-lg font-body font-semibold text-sm transition-all ${
                                    isActive
                                        ? "bg-ink text-cream"
                                        : "hover:bg-brand-yellow hover:-translate-y-0.5"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    {isAuthed ? (
                        <>
                            {user.role === "admin" && (
                                <Link
                                    to="/admin"
                                    data-testid="nav-admin-link"
                                    className="label-mono text-xs px-3 py-2 bg-brand-yellow border-2 border-ink rounded-lg hover:-translate-y-0.5 transition-transform"
                                >
                                    Admin
                                </Link>
                            )}
                            <span className="label-mono text-xs flex items-center gap-2">
                                <User size={14} /> {user.name}
                            </span>
                            <BrutalButton
                                size="sm"
                                variant="white"
                                onClick={logout}
                                data-testid="nav-logout-button"
                            >
                                <LogOut size={14} /> Logout
                            </BrutalButton>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid="nav-login-link"
                                className="font-body font-semibold text-sm px-4 py-2 hover:underline underline-offset-4"
                            >
                                Sign in
                            </Link>
                            <BrutalButton
                                as={Link}
                                to="/contact"
                                size="sm"
                                variant="primary"
                                data-testid="nav-cta-button"
                            >
                                Start a project
                            </BrutalButton>
                        </>
                    )}
                </div>

                <button
                    className="lg:hidden w-11 h-11 border-2 border-ink rounded-lg bg-white shadow-brutal-sm flex items-center justify-center"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t-4 border-ink bg-cream px-6 py-6 space-y-3" data-testid="nav-mobile-menu">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `block px-4 py-3 rounded-lg font-body font-semibold ${
                                    isActive ? "bg-ink text-cream" : "bg-white border-2 border-ink"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    {isAuthed ? (
                        <button
                            onClick={() => { setOpen(false); logout(); }}
                            data-testid="nav-mobile-logout-button"
                            className="w-full text-left px-4 py-3 rounded-lg bg-brand-orange text-white border-2 border-ink font-bold"
                        >
                            Logout ({user.name})
                        </button>
                    ) : (
                        <div className="flex gap-3 pt-2">
                            <Link
                                to="/login"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-login-link"
                                className="flex-1 text-center px-4 py-3 rounded-lg bg-white border-2 border-ink font-bold"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/contact"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-cta-link"
                                className="flex-1 text-center px-4 py-3 rounded-lg bg-brand-blue text-white border-2 border-ink font-bold"
                            >
                                Start
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </header>
    );
}
